/**
 * Swamithra — the list envelope.
 *
 * ONE PAGINATION STYLE, BECAUSE THE SERVICE HAS ONE. Every list takes `page`
 * and `page_size` and answers `{page, page_size, total, total_pages, has_more}`.
 * There is no cursor parameter and no `next_cursor` in any response.
 *
 * That matters most on mobile, where the same envelope drives a different
 * gesture. Web and the panel show NUMBERED controls with prev/next
 * (US-00-002.8, US-00-004.8, US-01-001.10); mobile APPENDS the next page on
 * infinite scroll (US-00-002.7) — which is page n + 1 here rather than a cursor.
 * A client that waited for a `next_cursor` the service never sends stopped at
 * the first page and showed a spinner that never resolved.
 *
 * `hasMore` is what an appending list reads, and it is derived from the page,
 * the size and the total when the service did not state it — the same
 * arithmetic the numbered controls already do.
 */

import { z } from "zod"

export const paginationSchema = z.object({
	/** Echoed effective page size — the server clamps, and says what it used. */
	limit: z.number().int().positive(),
	/**
	 * Total matching records. Present on both styles because the Expert
	 * dashboard has to show a result count that reflects the filters
	 * (US-01-001.2) and the user lists have to scope the count to the owner
	 * (US-00-006.2).
	 */
	totalCount: z.number().int().nonnegative(),
	hasMore: z.boolean(),
	/** Offset style. */
	page: z.number().int().positive().optional(),
	totalPages: z.number().int().nonnegative().optional(),
})

export type Pagination = z.infer<typeof paginationSchema>

/** The page size the service echoes when it did not say. */
const DEFAULT_PAGE_SIZE = 20

/**
 * Reads the service's list envelope into the one above.
 *
 * The service answers a list as records plus a total and a page size, flat, in
 * its own convention (`page_size`, `total`), which ./api/client has already
 * turned into `pageSize` and `total` by the time this runs. It does not send
 * `hasMore` — a client that waited for one would never load a second page on
 * mobile — so that is derived from the page, the size and the total, which is
 * the same arithmetic the numbered controls on web already do.
 *
 * Written as a normaliser rather than as three schemas because the alternative
 * is three list hooks that each unpack a slightly different shape, and the one
 * that gets it wrong is the one nobody pages past the first screen of.
 */
function normaliseEnvelope(value: unknown): unknown {
	if (value === null || typeof value !== "object") return value
	const record = value as Record<string, unknown>

	const data = record.data ?? record.records ?? record.items ?? record.results
	const nested = record.pagination
	const meta = (nested !== null && typeof nested === "object" ? nested : record) as Record<
		string,
		unknown
	>

	const count = Array.isArray(data) ? data.length : 0
	const limit = numberOr(meta.limit ?? meta.pageSize, count || DEFAULT_PAGE_SIZE)
	const totalCount = numberOr(meta.totalCount ?? meta.total, count)
	const page = numberOr(meta.page, undefined)
	const totalPages = numberOr(meta.totalPages, limit > 0 ? Math.ceil(totalCount / limit) : 0)
	const hasMore =
		typeof meta.hasMore === "boolean"
			? meta.hasMore
			: page !== undefined
				? page * limit < totalCount
				: count < totalCount

	return {
		data,
		pagination: {
			limit,
			totalCount,
			hasMore,
			...(page === undefined ? {} : { page }),
			totalPages,
		},
	}
}

function numberOr<T extends number | undefined>(value: unknown, fallback: T): number | T {
	return typeof value === "number" && Number.isFinite(value) ? value : fallback
}

/**
 * Wraps an item schema in the collection envelope.
 *
 *   const queryPage = paginated(querySummarySchema)
 */
export function paginated<T extends z.ZodTypeAny>(item: T) {
	return z.preprocess(
		normaliseEnvelope,
		z.object({
			data: z.array(item),
			pagination: paginationSchema,
		}),
	)
}

export type Page<T> = {
	data: T[]
	pagination: Pagination
}

/**
 * Query-string parameters shared by every list endpoint.
 *
 * There is no `cursor` here because there is no cursor on the wire. An
 * appending list asks for page n + 1.
 */
export type PageParams = {
	/** 1-based. */
	page?: number
	limit?: number
}

/**
 * Serialises page parameters, dropping the ones that are not set.
 *
 * `limit` goes over as `page_size`, which is what the service reads. Sent as
 * `limit` it is ignored, and an ignored page size is not an error — it is a list
 * that silently pages at whatever the server's default happens to be, which is
 * how the mobile infinite scroll and the numbered web controls end up disagreeing
 * about how many rows a page holds.
 */
export function pageQuery(params: PageParams = {}): Record<string, string | number> {
	const query: Record<string, string | number> = {}
	if (params.page !== undefined) query.page = params.page
	if (params.limit !== undefined) query.pageSize = params.limit
	return query
}

/**
 * The page an appending list should ask for next, or null when there is none.
 *
 * `hasMore` is the authority; the page number is worked out from the one just
 * read, so a list that started at page 3 from a restored screen still advances
 * correctly.
 */
export function nextPageOf(pagination: Pagination): number | null {
	if (!pagination.hasMore) return null
	return (pagination.page ?? 1) + 1
}
